/* eslint-disable max-len */

import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getSelectedCatalogItemsTotal } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { orderHasPendingPnAChecks } from 'entities/Order/lib/orderHasPendingPnAChecks/orderHasPendingPnAChecks';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import {
    getCanApproveSelectedOrder,
    getCanEditOwnSelectedOrder,
    getCanEditSelectedOrder,
    getCanRejectSelectedOrder,
} from 'entities/Order/model/selectors/ordersPermissions';
import {
    getSelectedOrder,
    getSelectedOrderId,
    getSelectedOrderIsLoading,
    getSelectedOrderStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import {
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { UserRole, getUserRole } from 'entities/User';
import {
    getUserCanAddOrderItem,
    getUserCanSeePrices,
} from 'entities/User/model/selectors/userPermissionsSeletors';
import { Vendor } from 'entities/Vendors';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { AddItemsToOrderButton } from 'features/AddItemsToOrder';
import { getProposalFilterIsActive } from 'features/OrderProposalFilter/model/selectors/orderProposalFilteSelectors';
import { RejectOrderButton } from 'features/changeOrderStatus';
import openBoxSearch from 'shared/assets/svg/open-box-search.svg';
import { getRouteSettingsOrdersSourcing } from 'shared/const/router';
import { EmptyPlaceholder } from 'shared/ui/EmptyPlaceholder/EmptyPlaceholder';
import {
    HelpWithOrderButton,
    useMiddleManAvailability,
} from 'widgets/MiddleMan';
import { OrderGroupedItems } from 'widgets/OrderGroupedItems';
import { SaveBarError } from 'widgets/OrderViewItemDetailed/lib';

import { OrderViewPageApproveButton } from './OrderViewPageApproveButton';

interface OrderViewPageItemsProps {
    vendors?: Vendor[];
    groupedItems: Record<string, CatalogItem[]>;
    onBack: () => void;
}

const checkProposalsForInactiveSuppliers = (
    proposals: Proposal[],
    vendorMap: Record<string, Vendor>,
) => {
    return proposals.every((proposal) => {
        const supplier = vendorMap[proposal.seller.uid];
        return supplier?.isOperationsActivated === false;
    });
};

const checkSelectedProposalsForInactiveSuppliers = (
    selectedProposals: SelectedProposal[],
    vendorMap: Record<string, Vendor>,
) => {
    return selectedProposals.some((proposal) => {
        if (proposal.selectedQuantity === 0) {
            return false;
        }

        const supplier = vendorMap[proposal.sellerUid];
        return supplier?.isOperationsActivated === false;
    });
};

export const OrderViewPageItems = ({
    vendors = [],
    groupedItems,
    onBack,
}: OrderViewPageItemsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const totalItemsCount = useSelector(getSelectedCatalogItemsTotal);
    const orderId = useSelector(getSelectedOrderId);
    const currentOrder = useSelector(getSelectedOrder);
    const orderStatus = useSelector(getSelectedOrderStatus);
    const orderIsLoading = useSelector(getSelectedOrderIsLoading);
    const canEditOwnSelectedOrder = useSelector(getCanEditOwnSelectedOrder);
    const canEditSelectedOrder = useSelector(getCanEditSelectedOrder);
    const canAddOrderItem = useSelector(getUserCanAddOrderItem);
    const canReject = useSelector(getCanRejectSelectedOrder);
    const filterIsActive = useSelector(getProposalFilterIsActive);
    const currentUserRole = useSelector(getUserRole);
    const { hasPendingChecks, itemsAwaitingPrices, itemsAwaitingAvailability } =
        orderHasPendingPnAChecks(currentOrder);
    const canApprove = useSelector(getCanApproveSelectedOrder);
    const canSeePrices = useSelector(getUserCanSeePrices);
    const selectedProperty = useSelector(getSelectedProperty);
    const vendorMap = useSelector(getPropertyVendorsMap);
    const { shouldShowHelpButton } = useMiddleManAvailability();

    const shouldSeeWarnings =
        !hasPendingChecks &&
        currentUserRole !== UserRole.MAINTENANCE_TECHNICIAN &&
        [
            OrderStatuses.DRAFT,
            OrderStatuses.IN_REVIEW,
            OrderStatuses.PENDING_APPROVAL,
        ].includes(orderStatus);

    const canAddItems =
        (canEditSelectedOrder && canAddOrderItem) || canEditOwnSelectedOrder;

    const hasItems = totalItemsCount > 0;

    const itemsWithSuppliersNotActivated = useMemo(() => {
        return Object.values(groupedItems)
            .flat()
            .filter((item) => {
                if (
                    !item.selectedProposals ||
                    item.selectedProposals.length === 0
                ) {
                    // If there are no selected proposals, that means either the checks haven't finished or the item is not sourced
                    // In that case, we need to check if all the proposals are not activated. If everything is not activated, this won't work and we need to show the error
                    return checkProposalsForInactiveSuppliers(
                        item.proposals,
                        vendorMap,
                    );
                }

                // Else wise we only care about the selected proposals
                return checkSelectedProposalsForInactiveSuppliers(
                    item.selectedProposals,
                    vendorMap,
                );
            });
    }, [groupedItems, vendorMap]);

    const itemsOverQuantity = useMemo(() => {
        return Object.values(groupedItems)
            .flat()
            .filter((item) => {
                if (!item.selectedProposals) {
                    return false;
                }

                const totalAvailable = item.proposals.reduce(
                    (acc, proposal) => acc + (proposal.availableQuantity || 0),
                    0,
                );

                const orderItemQty =
                    currentOrder.items.find(
                        (orderItem) => item.id === orderItem.id,
                    )?.qty || 0;

                return orderItemQty > totalAvailable;
            });
    }, [groupedItems, currentOrder]);

    const populateOperationsAccountStatus = (
        source: Record<string, CatalogItem[]>,
        suppliers: Vendor[],
    ): Record<string, CatalogItem[]> => {
        const output: Record<string, CatalogItem[]> = {};

        Object.keys(source).forEach((groupKey) => {
            output[groupKey] = source[groupKey].map((orderItem) => {
                if (!orderItem.selectedProposals) {
                    return orderItem;
                }

                // Deep clone the selected proposal to avoid mutating the original object
                const newOrderItem = {
                    ...orderItem,
                    selectedProposals: JSON.parse(
                        JSON.stringify(orderItem.selectedProposals),
                    ),
                };

                // Set the operations activated status for the proposal
                for (const proposal of newOrderItem.selectedProposals) {
                    const supplier = suppliers.find(
                        (s) => s.externalUuid === proposal.sellerUid,
                    );

                    if (supplier) {
                        proposal.isOperationsActivated =
                            supplier.isOperationsActivated;
                    }
                }

                return newOrderItem;
            });
        });

        return output;
    };

    const extendedGroupedItems = populateOperationsAccountStatus(
        groupedItems,
        vendors,
    );

    const canBeApproved = {
        flags: {
            activation: itemsWithSuppliersNotActivated.length === 0,
            quantity: itemsOverQuantity.length === 0,
        },
        messages: {
            quantity:
                itemsOverQuantity.length > 1
                    ? t(
                          'Requested quantities for some items exceed available stock.',
                      )
                    : t(
                          'Requested quantity for an item exceeds available stock.',
                      ),
        },
    };

    const showNoFilteredItems =
        filterIsActive &&
        Object.keys(groupedItems).length === 0 &&
        !orderIsLoading;

    const showHeader =
        !orderIsLoading && (canAddItems || (hasPendingChecks && canSeePrices));

    const getLoadingMessage = () => {
        const particles = [];

        if (itemsAwaitingAvailability) {
            particles.push(t('available quantities'));
        }
        if (itemsAwaitingPrices) {
            particles.push(t("your property's custom pricing"));
        }
        return `${t('Retrieving')} ${particles.join(t(' and '))}...`;
    };

    return (
        <Box height={'calc(100vh - 112px)'} position="relative">
            <Box p="16px 16px 96px" height="100%">
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent={!hasItems ? 'center' : undefined}
                    gap="8px"
                    height="100%"
                    sx={{
                        overflowY: 'scroll',
                        margin: '-10px',
                        padding: '10px',
                    }}
                >
                    {showHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '32px',
                                marginBottom: '8px',
                            }}
                        >
                            {canAddItems && <AddItemsToOrderButton />}
                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: '8px 10px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: '4px',
                                    border: '1px solid #F0C187',
                                    background: '#FFF6E6',
                                    color: '#B76500',
                                    textAlign: 'left',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    lineHeight: '16px',
                                    opacity:
                                        hasPendingChecks && canSeePrices
                                            ? 1
                                            : 0,
                                    transition: 'opacity 0.3s ease-in-out',
                                }}
                            >
                                {getLoadingMessage()}
                            </Box>
                        </Box>
                    )}

                    {shouldShowHelpButton && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                marginBottom: '8px',
                            }}
                        >
                            <HelpWithOrderButton />
                        </Box>
                    )}

                    {showNoFilteredItems && (
                        <Typography
                            pb="24px"
                            sx={{ opacity: 0.5 }}
                            typography="openSans.body2Medium"
                        >
                            {t('There is no Items in Selected Filter')}.
                        </Typography>
                    )}
                    {!(hasItems && orderIsLoading) &&
                        Object.entries(extendedGroupedItems).map(
                            ([categoryId, items]) => (
                                <OrderGroupedItems
                                    key={categoryId}
                                    items={items}
                                    categoryId={categoryId}
                                />
                            ),
                        )}
                    <Box
                        height="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {!hasItems && !orderIsLoading && (
                            <Box width="366px">
                                <EmptyPlaceholder image={openBoxSearch}>
                                    <Typography
                                        mt="16px"
                                        sx={{ opacity: 0.6 }}
                                        typography="openSans.body2Medium"
                                        align="center"
                                    >
                                        {t(
                                            "The order doesn't have any items yet, so you can't confirm or reject an empty order",
                                        )}
                                        .
                                    </Typography>
                                </EmptyPlaceholder>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                p="12px 16px 16px"
                bgcolor="background.paper"
                position="absolute"
                width="100%"
                bottom="0px"
                borderRadius="16px"
            >
                <Box display="flex" sx={{ alignItems: 'center' }}>
                    {shouldSeeWarnings && (
                        <>
                            {!canBeApproved.flags.activation && (
                                <SaveBarError
                                    message={t(
                                        'must be enabled to submit order.',
                                    )}
                                    actionText={t('Supplier accounts')}
                                    onAction={() => {
                                        if (selectedProperty?.id) {
                                            navigate(
                                                getRouteSettingsOrdersSourcing(
                                                    selectedProperty.id,
                                                    'operations',
                                                ),
                                            );
                                        }
                                    }}
                                />
                            )}
                            {!canBeApproved.flags.quantity &&
                                canBeApproved.flags.activation && (
                                    <SaveBarError
                                        message={
                                            canBeApproved.messages.quantity
                                        }
                                    />
                                )}
                        </>
                    )}
                </Box>
                <Box display="flex" flexDirection="column">
                    <Box display="flex" alignSelf={'flex-end'} gap="16px">
                        {canReject && orderStatus !== 'DRAFT' && (
                            <RejectOrderButton
                                orderId={orderId}
                                disabled={
                                    !hasItems ||
                                    (hasPendingChecks && canApprove)
                                }
                            />
                        )}
                        <OrderViewPageApproveButton
                            canBeApproved={Object.values(
                                canBeApproved.flags,
                            ).every((value: boolean) => value === true)}
                            isNoItemsInOrder={!hasItems}
                            hasLoadingPnA={hasPendingChecks}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
